import React, { useEffect } from "react";
import { useIntl, IntlShape } from "react-intl";
import { GridRowModel, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { Form, Formik, useFormikContext } from "formik";
import { DateTime } from "luxon";
import * as Yup from "yup";

import { GenericObject } from "../services/Price";
import { isObject } from "../services/utils";
import { FormBuilder } from "./FormBuilder";
import {
  ConfigurationFormType,
  FormDetailsType,
  ConfigurationPageTable,
  CalculEditorFormDetails,
  StaticSelectFormDetails,
  MultiApiTypeFormDetails,
  FieldGroupColumnFormDetailsFilled,
  FieldGroupColumnFormDetails,
  RemoteLookupFormDetails,
} from "./interfaces";
import { reducePath } from "./utils";
import { mergeArr } from "./modalInputs/FieldGroupColumn";

// We are using a promise to load mathjs parse function asynchronously because mathjs
// is a heavylibrary and we want to avoid loading it if user don't use CalculEditor input
let mathjsParsePromise: Promise<(expr: string) => any> | null = null;
function getMathjsParse() {
  if (!mathjsParsePromise) {
    mathjsParsePromise = import("mathjs")
      .then(({ parse }) => parse)
      .catch((err) => {
        mathjsParsePromise = null; // clear cache so next call retries
        return Promise.reject(err);
      });
  }
  return mathjsParsePromise;
}

const MAX_NODES = 200; // Limit node to MathJs Ast to avoid (DoS)
const MAX_DEPTH = 20; // Limit Depth to MathJs Ast to avoid (DoS)

/**
 * Merges two objects completely
 *
 * This is not a shallow merge contrary to `Object.assign`.
 * It will merge the objects recursively instead of replacing
 * the references.
 *
 * TODO: use lodash deep merge instead as this is just c/c
 *  from stackoverflow and it does not protect against
 *  circular references.
 * @param target
 * @param source
 */
export default function mergeDeep(
  target: GenericObject,
  source: GenericObject
) {
  let output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) Object.assign(output, { [key]: source[key] });
        else output[key] = mergeDeep(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

/**
 * Reports the form's dirty state (Formik's built-in `dirty` flag) to the parent
 * after each render. Because the Formik <Formik> wrapper uses `enableReinitialize`
 * and a `key` derived from initialValues, `dirty` resets to false whenever the
 * form is re-seeded with server-computed values — which is the signal we use
 * to re-enable the Save button after a successful Calculate.
 */
const DirtyTracker = ({
  onDirtyChange,
}: {
  onDirtyChange: (isDirty: boolean) => void;
}) => {
  const { dirty } = useFormikContext();

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  return null;
};

/**
 * Creates the initial values for Formik form
 *
 * We are interpreting `opt.key` event if it's a dotted path.
 * Example: `some.object` => {"some":{"object": 1}}, val = 1
 *
 * We replace "." in path field (for example: "property.uhCat") by "__", it's because we have to work around the object
 * being merged by Formik when setting dotted path. With "somehting.uh_cat" and "something.uh_cat.property",
 * when setting uh_cat, "uh_cat.property" is overridden.
 *
 * In case of row, fill input with table row value.
 * In case of rows, if selected rows has same comlumn value fill input, if not set empty value.
 * In case of creation (no row and rows selected), set empty value.
 *
 * @param fieldsOptions
 * @param row
 * @param rows
 */
export const createInitialValues = (
  fieldsOptions: FormDetailsType,
  parentFormValues: GenericObject | null,
  filters?: GenericObject,
  row?: GridRowModel,
  rows?: GridRowModel[]
) => {
  const data: any = {};
  for (const opt of fieldsOptions) {
    const segments = opt.key.split(".");
    if (opt.type === ConfigurationFormType.FieldGroup) {
      const key = segments.join("__");
      if (row && Array.isArray(row[opt.key])) {
        data[key] = row[opt.key].map((item: any) => {
          return createInitialValues(opt.fields, null, filters, item);
        });
      } else {
        data[key] = [];
      }
    } else if (opt.type === ConfigurationFormType.FieldGroupColumn) {
      const optData = opt as FieldGroupColumnFormDetails;
      const key = segments.join("__");
      const mergedFields = mergeArr(optData);
      if (row && Array.isArray(row[opt.key]) && row[opt.key].length > 0) {
        data[key] = row[opt.key].map((item: any) => {
          return createInitialValues(mergedFields, null, filters, item);
        });
      } else if (optData.limitMin || optData.limitDefault) {
        const initCount = optData.limitDefault ?? optData.limitMin ?? 0;
        data[key] = [];
        for (let i = 0; i < initCount; i++) {
          data[key].push(createInitialValues(mergedFields, null, filters));
        }
        if (optData.autoFillPercent && initCount > 0) {
          const base = Math.floor(10000 / initCount);
          const remainder = 10000 - base * (initCount - 1);
          data[key].forEach((item: any, i: number) => {
            optData.autoFillPercent!.forEach((field: string) => {
              item[field] = (
                (i < initCount - 1 ? base : remainder) / 100
              ).toFixed(2);
            });
          });
        }
      } else {
        data[key] = [];
      }
    } else {
      let val: any;
      if (!row) {
        if (opt.type === ConfigurationFormType.Check) {
          val = !!opt.defaultValue;
        } else if (opt.type === ConfigurationFormType.DatePicker) {
          val = DateTime.now().toFormat("yyyy-MM-dd");
        } else if (opt.type === ConfigurationFormType.DateRangePicker) {
          if (opt.defaultValue === null || opt.defaultValue === undefined) {
            val = {
              [opt.startDateField]: null,
              [opt.endDateField]: null,
            };
          } else {
            val = {
              [opt.startDateField]: DateTime.now(),
              [opt.endDateField]: DateTime.now().plus({ days: 1 }),
            };
          }
        } else {
          if (opt.watchLookup && filters) {
            val = filters[opt.watchLookup] ? filters[opt.watchLookup] : "";
          } else if (
            opt.defaultValue !== null ||
            opt.defaultValue !== undefined
          ) {
            val = opt.defaultValue;
          } else {
            val = "";
          }
        }

        if (rows && rows[0]) {
          const firstRow = rows[0];
          const isNotSameValue = rows.some((el) => {
            const firstValue = firstRow[opt.key];
            const elValue = el[opt.key];

            if (isObject(firstValue)) {
              return JSON.stringify(firstValue) !== JSON.stringify(elValue);
            } else {
              return firstValue !== el[opt.key];
            }
          });

          if (opt.type === ConfigurationFormType.DateRangePicker) {
            const isNotSameDateValueOf = (field: string) => {
              return rows.some((el) => {
                return firstRow[field] !== el[field];
              });
            };

            val = {
              [opt.startDateField]: isNotSameDateValueOf(opt.startDateField)
                ? null
                : DateTime.fromISO(rows[0][opt.startDateField]),
              [opt.endDateField]: isNotSameDateValueOf(opt.endDateField)
                ? null
                : DateTime.fromISO(rows[0][opt.endDateField]),
            };
          } else if (!isNotSameValue) {
            val = firstRow[opt.key];
          }
        }
      } else {
        if (opt.initialValueFromRow) {
          val = opt.initialValueFromRow(row);
        } else if (opt.type === ConfigurationFormType.Input) {
          if (row[opt.key] !== null && row[opt.key] !== undefined) {
            if (opt.inputType === "percent") {
              val = parseFloat((row[opt.key] * 100).toFixed(2));
            } else if (opt.inputType === "negativeInteger") {
              val = Math.abs(row[opt.key]);
            } else {
              val = row[opt.key];
            }
          } else if (
            opt.defaultValue !== null &&
            opt.defaultValue !== undefined
          ) {
            val = opt.defaultValue;
          } else {
            val = "";
          }
        } else if (opt.type === ConfigurationFormType.DateRangePicker) {
          val = {
            [opt.startDateField]:
              row[opt.startDateField] === null
                ? null
                : DateTime.fromISO(row[opt.startDateField]),
            [opt.endDateField]:
              row[opt.endDateField] === null
                ? null
                : DateTime.fromISO(row[opt.endDateField]),
          };
        } else if (opt.type === ConfigurationFormType.PreviewBlock) {
          val = {
            value: reducePath(row, opt.key.split(".")),
            datasPreview: opt.datasPreview.map((field: GenericObject) => ({
              ...field,
              value: reducePath(row, field.key.split(".")),
            })),
          };
        } else if (opt.type === ConfigurationFormType.ErrorMessage) {
          if (opt.dynamicLabelValueKey) {
            val = {
              value: reducePath(row, opt.key.split(".")),
              errorMessageValue: reducePath(
                row,
                opt.dynamicLabelValueKey.split(".")
              ),
            };
          } else {
            val = reducePath(row, opt.key.split("."));
          }
        } else if (opt.type === ConfigurationFormType.MultiSelect) {
          const watchFieldData = row[opt.multiApi.watchFilter];
          const currApiDataMap = opt.multiApi.apisFieldMap[watchFieldData];

          if (currApiDataMap) {
            val = row[opt.key].map(
              (data: GenericObject) => data[currApiDataMap].id
            );
          } else {
            val = null;
          }

          if (
            opt.interpretNullAsAll &&
            (row[opt.key] === null || row[opt.key].length === 0)
          ) {
            val = ["ALL"];
          }
        } else if (opt.type === ConfigurationFormType.StaticSelect) {
          const currOpt = opt as StaticSelectFormDetails;
          if (currOpt.interpretNullAsAll && row[opt.key] === null) {
            val = "ALL";
          } else {
            val = reducePath(row, segments);
          }
        } else if (
          opt.type === ConfigurationFormType.Select &&
          opt.apiResultsType
        ) {
          // Use this to fix language selecor in mail form
          val = {
            id: reducePath(row, segments),
            label: reducePath(row, segments),
          };
        } else {
          val = reducePath(row, segments);
        }
      }

      if (
        isObject(val) &&
        opt.type !== ConfigurationFormType.DateRangePicker &&
        opt.type !== ConfigurationFormType.PreviewBlock &&
        opt.type !== ConfigurationFormType.ErrorMessage
      ) {
        // passing the full object
        // we might try passing the id but autocomplete force the same type
        // for the options and the value so object it is
        // data[opt.key] = val.id ? val[opt.labelKey as string] : null;

        // casting because labelKey should always exist when it is an object
        if (opt.type === ConfigurationFormType.Input) {
          // we check for labelKey if we want to display just the object name
          // data[opt.key] = opt.labelKey ? val[opt.labelKey] : val;
          val = opt.labelKey ? val[opt.labelKey] : val;
        } else {
          if (opt.keepApiData) {
            const keepDataVal: Record<string, any> = val.id
              ? { id: val.id, label: val[opt.labelKey as string] }
              : {};
            opt.keepApiData.forEach((key) => (keepDataVal[key] = val[key]));
            val = keepDataVal;
          } else {
            val = val.id
              ? { id: val.id, label: val[opt.labelKey as string] }
              : null;
          }
        }
      }

      const key = segments.join("__");
      data[key] = val;
    }
  }

  return parentFormValues ? parentFormValues : data;
};

function createYupSchema(fieldsOptions: FormDetailsType, intl: IntlShape) {
  const data: any = {};

  for (const opt of fieldsOptions) {
    let validator: any = null;
    if (opt.type === ConfigurationFormType.Input) {
      if (
        opt.inputType === "number" ||
        opt.inputType === "percent" ||
        opt.inputType === "currency"
      ) {
        validator = Yup.number();
      } else if (
        opt.inputType === "integer" ||
        opt.inputType === "negativeInteger"
      ) {
        validator = Yup.number().integer();
      } else if (opt.inputType === "email") {
        validator = Yup.string().email();
      } else {
        validator = Yup.string();
      }
    } else if (opt.type === ConfigurationFormType.Check) {
      validator = Yup.boolean();
    } else if (opt.type === ConfigurationFormType.Select) {
      if (opt.freeSolo && opt.inputType === "email") {
        // In case of freeSolo mode, value can be an object or a string
        validator = Yup.mixed().test(
          "is-mail-string-or-object",
          intl.formatMessage({ id: "EMAIL_INPUT_ERROR" }),
          (value) => {
            if (typeof value === "object" && value !== null) {
              let valueObj: any = value;
              // Validate email in object
              return Yup.string().email().isValidSync(valueObj.label);
            }
            if (typeof value === "string") {
              // Validate string as email
              return Yup.string().email().isValidSync(value);
            }
            return false;
          }
        );
      } else {
        validator = Yup.object();
      }
    } else if (opt.type === ConfigurationFormType.RemoteLookup) {
      const lookupOpt = opt as RemoteLookupFormDetails;
      validator = Yup.mixed().test(
        "remote-lookup-found",
        intl.formatMessage({ id: lookupOpt.notFoundLabel }),
        (value) => value != null
      );
    } else if (opt.type === ConfigurationFormType.DayOfWeek) {
      validator = Yup.number();
    } else if (opt.type === ConfigurationFormType.DatePicker) {
      validator = Yup.date();
    } else if (opt.type === ConfigurationFormType.DateRangePicker) {
      validator = Yup.object()
        .shape({
          [opt.startDateField]: opt.required
            ? Yup.date().nullable().required()
            : Yup.date().nullable(),
          [opt.endDateField]: opt.required
            ? Yup.date().nullable().required()
            : Yup.date().nullable(),
        })
        .test(
          "isFilled",
          intl.formatMessage({ id: "DATE_INPUTS_FILLED_CONDITION" }),
          (value) => {
            if (!opt.required) return true;
            if (
              (value[opt.startDateField] && !value[opt.endDateField]) ||
              (!value[opt.startDateField] && value[opt.endDateField])
            ) {
              return false;
            }
            return true;
          }
        );
    } else if (
      opt.type === ConfigurationFormType.StaticSelect ||
      opt.type === ConfigurationFormType.MultiSelect
    ) {
      const currOpt = opt as StaticSelectFormDetails | MultiApiTypeFormDetails;
      if (currOpt.multiple) {
        validator = Yup.array().of(Yup.string());
      } else {
        validator = Yup.string();
      }
    } else if (opt.type === ConfigurationFormType.SelectValues) {
      validator = Yup.array().of(Yup.object());
      validator = opt.required ? validator.min(1) : validator.nullable();
    } else if (opt.type === ConfigurationFormType.FieldGroupColumn) {
      const optData = opt as FieldGroupColumnFormDetailsFilled;
      const mergeFields = mergeArr(optData);
      const nestedSchema = createYupSchema(mergeFields, intl);
      const baseArraySchema = Yup.array().of(nestedSchema);

      if (
        opt.relatedInputAction?.relatedInput &&
        typeof opt.relatedInputAction.relatedInput === "string" &&
        opt.relatedInputAction.action === "show"
      ) {
        const relatedField = opt.relatedInputAction.relatedInput
          .split(".")
          .join("__");

        const checkRequiredFields = baseArraySchema.when(
          relatedField,
          ([relatedValue]: boolean[], schema: any) => {
            if (relatedValue !== true) return schema.strip();
            return optData.limitMin
              ? schema.min(
                  optData.limitMin,
                  intl.formatMessage({
                    id: "CONNECTING_ROOM_INPUTS_NB_FORM_ERROR",
                  })
                )
              : schema;
          }
        );

        if (optData.sumPercentLimit) {
          const percentLimitFields = Array.isArray(optData.sumPercentLimit)
            ? optData.sumPercentLimit
            : [optData.sumPercentLimit];
          validator = checkRequiredFields.test(
            "sum-check",
            intl.formatMessage({
              id: "CONNECTING_ROOM_TOTAL_CA_INVALID_ERROR",
            }),
            function (value) {
              if (!value || !Array.isArray(value)) return true;

              return percentLimitFields.every((field) => {
                const sum = value.reduce(
                  (acc, curr) => acc + Number(curr[field]),
                  0
                );
                return sum === 100;
              });
            }
          );
        } else {
          validator = checkRequiredFields;
        }
      } else {
        validator = baseArraySchema;
      }
    } else if (opt.type === ConfigurationFormType.FieldGroup) {
      const nestedSchema = createYupSchema(opt.fields, intl);

      validator = Yup.array().of(nestedSchema);
    } else if (opt.type === ConfigurationFormType.CalculEditor) {
      const formOpt = opt as CalculEditorFormDetails;

      validator = Yup.string().test(
        "isValidCalcul",
        intl.formatMessage({ id: "CALCUL_EDITOR_INPUT_ERROR_FORMAT" }),
        async function (value) {
          if (!value) return false;
          const parse = await getMathjsParse();

          const allowedVars =
            formOpt.editorInputs
              ?.filter((input) => input.isVariable)
              .map((variable) => variable.value) ?? [];

          const requiredInput =
            formOpt.editorInputs
              ?.filter((input) => input.required)
              .map((input) => ({
                value: input.value,
                label: intl.formatMessage({ id: input.label }),
              })) ?? [];

          try {
            // Check value length
            if (formOpt.inputMaxLength && value.length > formOpt.inputMaxLength)
              return false;

            // Check if all Char in value are allowed
            const allowedChar = /^[0-9+\-*/()., a-zA-Z_]+$/;
            if (!allowedChar.test(value))
              return this.createError({
                message: intl.formatMessage({
                  id: "CALCUL_EDITOR_FORBIDEN_CHAR_ERROR",
                }),
              });

            // Check if value doesn't end with a forbidden value
            const forbiddenCharAtEnd = /[+\-*/.,]$/;
            if (forbiddenCharAtEnd.test(value.trim()))
              return this.createError({
                message: intl.formatMessage({
                  id: "CALCUL_EDITOR_EXPRESSION_ENDED_WITH_OPERATOR_ERROR",
                }),
              });

            // Check if value doesn't have multiple operators in succession
            const forbiddenPattern = /([+\-*/.,]{2,})/;
            if (forbiddenPattern.test(value))
              return this.createError({
                message: intl.formatMessage({
                  id: "CALCUL_EDITOR_SEQUENCE_OPERATOR_ERROR",
                }),
              });

            // Transform "," in "." beacause MathJs don't allow ","
            const formattedValue = value.replaceAll(",", ".");
            const parsedValue = parse(formattedValue);

            const symbols: string[] = [];
            let nodeCount = 0;
            let isValid = true;

            const traverse = (node: any, depth: number = 0) => {
              if (!isValid) return;
              nodeCount++;

              // To much nodes
              if (nodeCount > MAX_NODES) {
                isValid = false;
                return;
              }

              // To much depth
              if (depth > MAX_DEPTH) {
                isValid = false;
                return;
              }

              // Function are not allowed
              if (node.isFunctionNode) {
                isValid = false;
                return;
              }

              // Avoid accessorNode (ex: a.b, arr[0] ...)
              if (node.isAccessorNode || node.isIndexNode) {
                isValid = false;
                return;
              }

              if (node.isSymbolNode) symbols.push(node.name);
              if (node.args) node.args.forEach(traverse, depth + 1);
              if (node.content) traverse(node.content, depth + 1);
            };
            traverse(parsedValue);

            if (!isValid)
              return this.createError({
                message: intl.formatMessage({
                  id: "CALCUL_EDITOR_EXPRESSION_TOO_COMPLEX_ERROR",
                }),
              });

            // Checks if the found custom variables are all allowed
            if (!symbols.every((v) => allowedVars.includes(v)))
              return this.createError({
                message: intl.formatMessage({
                  id: "CALCUL_EDITOR_FORBIDEN_VARIABLE_ERROR",
                }),
              });

            // Check if all required input are present
            if (!requiredInput.every((input) => symbols.includes(input.value)))
              return this.createError({
                message: intl.formatMessage(
                  { id: "CALCUL_EDITOR_REQUIRED_VARIABLE_ERROR" },
                  {
                    values: requiredInput
                      .map((input) => input.label)
                      .join(", "),
                  }
                ),
              });

            return true;
          } catch (err) {
            console.error("Calcul invalid", err);
            return false;
          }
        }
      );
    }

    if (validator) {
      if (opt.type !== ConfigurationFormType.FieldGroup) {
        // If it's not required it should be ok to set it null
        // Moreover, when required add nullable to chain because of error message https://stackoverflow.com/a/51233885
        validator = opt.required
          ? validator.required().nullable()
          : validator.nullable();
      }

      // A field hidden by a value-based show/hide must not block the submit
      // of the mode it does not belong to: relax it while it is hidden.
      const relatedAction = opt.relatedInputAction;
      if (
        relatedAction &&
        relatedAction.equals !== undefined &&
        typeof relatedAction.relatedInput === "string" &&
        (relatedAction.action === "show" || relatedAction.action === "hide")
      ) {
        const relatedKey = relatedAction.relatedInput.split(".").join("__");
        const expected = relatedAction.equals;
        const hiddenWhenEqual = relatedAction.action === "hide";
        // Replaced, not relaxed: `notRequired()` would keep the custom tests
        // (e.g. a lookup's "found" check) alive while the field is hidden.
        validator = validator.when(
          relatedKey,
          ([related]: unknown[], schema: any) =>
            (related === expected) === hiddenWhenEqual
              ? Yup.mixed().nullable()
              : schema
        );
      }

      const key = opt.key.split(".").join("__");
      data[key] = validator;
    }
  }

  return Yup.object().shape(data);
}

const useCreateInitialValues = (
  fieldsOptions: FormDetailsType,
  parentFormValues: GenericObject | null,
  filters?: GenericObject,
  row?: GridRowModel,
  rows?: GridRowModel[],
  onDisplaySubCreationForm?: boolean
) =>
  createInitialValues(
    fieldsOptions,
    onDisplaySubCreationForm ? parentFormValues : null,
    filters,
    onDisplaySubCreationForm ? row : undefined,
    onDisplaySubCreationForm ? rows : undefined
  );

export const useYupSchema = (fieldsOptions: FormDetailsType) => {
  const intl = useIntl();

  return createYupSchema(fieldsOptions, intl);
};

export const getSubmitValue = (
  fieldsOptions: FormDetailsType,
  values: any,
  row?: GridRowModel
) => {
  const newData: Record<string, any> = {};

  for (const opt of fieldsOptions) {
    if (
      opt.virtual ||
      opt.type === ConfigurationFormType.RemoteLookup ||
      opt.type === ConfigurationFormType.Chart
    ) {
      continue;
    }

    // A box disabled by the form state sends what it shows: unticked.
    if (
      opt.type === ConfigurationFormType.Check &&
      opt.disabledIf?.(row, values)
    ) {
      newData[opt.key] = false;
      continue;
    }

    if (
      opt.type === ConfigurationFormType.MultiSelect ||
      opt.type === ConfigurationFormType.StaticSelect
    ) {
      const option = opt as StaticSelectFormDetails | MultiApiTypeFormDetails;
      const optionKey = option.key.split(".").join("__");

      // In case of 'ALL' backend want to get null value
      if (option.multiple && values[optionKey]) {
        const allValueIndex = values[optionKey].findIndex(
          (value: string) => value === "ALL"
        );

        if (allValueIndex !== -1) {
          newData[optionKey] = null;
        } else {
          newData[optionKey] = values[optionKey];
        }
      } else {
        if (values[optionKey] === "ALL") {
          newData[optionKey] = null;
        } else {
          newData[optionKey] = values[optionKey];
        }
      }
    } else if (opt.type === ConfigurationFormType.DateRangePicker) {
      for (const [key, date] of Object.entries(
        values[opt.key] as {
          [key: string]: DateTime;
        }
      )) {
        newData[key] = date ? date.toISODate() : null;
      }
    } else if (opt.type === ConfigurationFormType.FieldGroup) {
      const optionKey = opt.key.split(".").join("__");
      const groupEntries = values[optionKey] || [];
      newData[opt.key] = groupEntries.map((entry: any) => {
        return getSubmitValue(opt.fields, entry);
      });
    } else if (opt.type === ConfigurationFormType.FieldGroupColumn) {
      const optionKey = opt.key.split(".").join("__");
      const mergeFields = mergeArr(opt as FieldGroupColumnFormDetails);

      if (
        opt.relatedInputAction?.action === "show" &&
        typeof opt.relatedInputAction.relatedInput === "string" &&
        !values[opt.relatedInputAction.relatedInput.split(".").join("__")]
      ) {
        newData[opt.key] = [];
      } else {
        const groupEntries = values[optionKey] || [];
        newData[opt.key] = groupEntries.map((entry: any) => {
          return getSubmitValue(mergeFields, entry);
        });
      }
    } else if (
      opt.type === ConfigurationFormType.PreviewBlock &&
      values[opt.key]
    ) {
      newData[opt.key] = values[opt.key].value;
      values[opt.key].datasPreview.forEach((data: GenericObject) => {
        let value = null;
        if (data.value || data.value === 0) {
          // If number has decimal return only 2 decimal
          value =
            Number(data.value) % 1 === 0
              ? data.value
              : Number(data.value).toFixed(2);
        }
        return (newData[data.key] = value);
      });
    } else if (
      opt.type === ConfigurationFormType.ErrorMessage &&
      values[opt.key]
    ) {
      newData[opt.key] = values[opt.key].value;
    } else if (opt.type === ConfigurationFormType.SelectValues) {
      const optionKey = opt.key.split(".").join("__");
      const value = values[optionKey];
      if (Array.isArray(value)) {
        newData[opt.key] = value.filter((v: any) => !v.isHardCodedOption);
      } else {
        newData[opt.key] = value;
      }
    } else if (opt.type !== ConfigurationFormType.Separator) {
      const optionKey = opt.key.split(".").join("__");
      const value = values[optionKey] === "" ? null : values[optionKey];

      if (value && value.isHardCodedOption) {
        newData[opt.key] = value;
      } else {
        if (opt.type === ConfigurationFormType.Select) {
          if (isObject(value)) {
            newData[opt.key] = opt.dataKey ? value[opt.dataKey] : value.id;
          } else {
            newData[opt.key] = value;
          }
        } else {
          if (
            ConfigurationFormType.Input &&
            opt.inputType === "percent" &&
            value
          ) {
            newData[opt.key] = parseFloat((parseFloat(value) / 100).toFixed(4));
          } else if (
            ConfigurationFormType.Input &&
            opt.inputType === "negativeInteger" &&
            value
          ) {
            newData[opt.key] = -value;
          } else if (opt.type === ConfigurationFormType.Check) {
            newData[opt.key] = value || false;
          } else {
            if (opt.skipIfEmpty && !value) {
              // Do nothing
            } else {
              newData[opt.key] = isObject(value) ? value.id : value;
            }
          }
        }
      }
    }
  }

  return newData;
};

export const EditForm = ({
  filters,
  fieldsOptions,
  onSubmit,
  row,
  dynamicPath,
  rows,
  multipleSelection,
  isMappingUhCat,
  onDisplaySubCreationForm,
  parentFormValues,
  onDirtyChange,
  initialValues: initialValuesOverride,
}: {
  filters?: GenericObject;
  // Values the caller imposes over the ones derived from the row and the
  // fields (form notation keys), e.g. the mode a dialog opens in.
  initialValues?: GenericObject;
  fieldsOptions: FormDetailsType;
  onSubmit: (data: any) => void;
  row?: GridRowModel;
  dynamicPath?: boolean;
  rows?: GridRowModel[];
  multipleSelection?: GridRowSelectionModel;
  isMappingUhCat?: boolean;
  onDisplaySubCreationForm?: (
    formOptions: ConfigurationPageTable,
    parentFormValues: GenericObject,
    initialValues: GenericObject
  ) => void;
  parentFormValues: GenericObject | null;
  onDirtyChange?: (isDirty: boolean) => void;
}) => {
  const initialValues = {
    ...useCreateInitialValues(
      fieldsOptions,
      parentFormValues,
      filters,
      row,
      rows,
      !!onDisplaySubCreationForm
    ),
    ...initialValuesOverride,
  };
  const yupSchema = useYupSchema(fieldsOptions);

  const handleSubmit = (values: any) => {
    onSubmit(getSubmitValue(fieldsOptions, values, row));
  };

  return (
    <Formik
      enableReinitialize
      // Use key to help formik to identify different forms and help reseting form value when necessary
      key={JSON.stringify(initialValues)}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values);
        setSubmitting(false);
      }}
      validateOnChange={false}
      initialValues={initialValues}
      validationSchema={yupSchema}
    >
      {({ validateForm }) => (
        <Form
          id="create-filter-form"
          onChange={() => {
            validateForm();
          }}
        >
          {onDirtyChange && <DirtyTracker onDirtyChange={onDirtyChange} />}
          <FormBuilder
            fieldsOptions={fieldsOptions}
            row={row}
            rows={rows}
            multipleSelection={multipleSelection}
            isMappingUhCat={isMappingUhCat}
            dynamicPath={dynamicPath}
            onDisplaySubCreationForm={onDisplaySubCreationForm}
          />
        </Form>
      )}
    </Formik>
  );
};
